/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, createContext, useContext, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useIdleTimer } from "react-idle-timer";
import fetchHelper from "../services/fetchHelper";
import { LocationDialogContext } from "./LocationDialog";
import { useHistory, NavLink } from "react-router-dom";
import { getRandomInt } from "../utils/inject";
import useKeyboardShortcut from "../useKeyboardShortcut";
import { version } from "../../package.json";
import { logout } from "../auth/utils";
import { featureCheck } from "../utils/feature";
import { fetchAppConfigToggles } from "../utils/appConfigHelper";
import ModalAgreements from "./UserAgreements";

export const LayoutContext = createContext({});

const Layout = ({ children }) => {
  const history = useHistory();
  const { addToast } = useToasts();
  const [user, setUser] = useState(null);
  const [assignerInfo, setAssignerInfo] = useState(null);
  const [mfrData, setMFRData] = useState(null);
  const [headerDetails, setHeaderDetails] = useState(null);
  const [pickLocs, setPickLocs] = useState([]);
  const [readPhysicians, setReadPhysicians] = useState([]);
  const { showLocationDialog, hideLocationDialog, setIsSucceed, setIsLoading } =
    useContext(LocationDialogContext);
  const [currentLocation, setCurrentLocation] = useState({});
  const [hasAdtIntegration, setHasAdtIntegration] = useState(false);
  const [patientEngagementTask, setPatientEngagementTask] = useState(false);
  const [intrOrderInfo, setIntrOrderInfo] = useState({});
  const [shouldShowEnrollment, setShouldShowEnrollment] = useState(false);
  const [appConfigStructure, setAppConfigStructure] = useState({});
  const [isUserAndToggleReady, setUserAndToggleReady] = useState(false);

  useKeyboardShortcut(["Shift", "v"], () => console.log(`Geneva (Version: ${version})`));

  useEffect(() => {
    setHasAdtIntegration(!!currentLocation.adt);
    enablePatientEngagementTask();
  }, [currentLocation.adt]);

  useEffect(() => {
    setShouldShowEnrollment(user?.locations?.length === 1 && currentLocation.enrollmentUrl);
  }, [user, currentLocation]);

  /** Handle APP Idle */
  const handleOnIdle = () => {
    logout(history.location.pathname);
  };

  /** To Enable or Disable Task Queues Tab */
  const enablePatientEngagementTask = () => {
    setPatientEngagementTask(featureCheck("patientEngagementFeature.enable"));
  };

  useIdleTimer({
    timeout: 1000 * 60 * 20,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const onPressCancel = () => {
    hideLocationDialog();
  };

  const changeLocation = (e) => {
    e && e.preventDefault && e.preventDefault();
    localStorage.setItem("selectedTab", "unprocessed");
    window.location.replace(`/reports3/unprocessed?${getRandomInt(1000, 9999)}`);
  };

  const onClickLocation = async (loc) => {
    showLocationDialog({
      onPressSave: async () => {
        try {
          setIsLoading(true);
          await fetchHelper.put("/patientmanagement/rest/picklocation/" + loc.id + "/select");
          setIsLoading(false);
          localStorage.setItem("location", loc.id);
        } catch (err) {
          setIsLoading(false);
          console.log(err);
        }
        setIsSucceed(true);
        changeLocation();
        hideLocationDialog();
      },
      onPressCancel,
    });
  };

  const fetchAppConfig = async () => {
    let value = await fetchAppConfigToggles();

    setAppConfigStructure(value);
  };

  const fetchU = async () => {
    try {
      let url3 = "/patientmanagement/rest/users/currentUser";
      const res3 = await fetchHelper.get({ url: url3 });
      if (process.env.REACT_APP_ENABLE_ANALYTICS == "true") {
        // eslint-disable-next-line no-undef
        pendo.initialize({
          visitor: {
            id: res3.id,
            hub: res3.hub,
            admin: res3.admin,
            email: res3.email,
            roles: res3.groups.map(({ name }) => name),
            locations: res3.locations.map(({ name }) => name),
            createdAt: res3.createdAt,
            genAdmin: res3.genAdmin,
            isServiceReport: res3.isServiceReport,
            prepper: res3.prepper,
            rcc: res3.rcc,
            rtDefibrillators: res3.rtDefibrillators,
            rtPaceMakers: res3.rtPaceMakers,
            superAdmin: res3.superAdmin,
          },
          account: {
            id: `${process.env.REACT_APP_ENV}-geneva-env`,
          },
        });
      }
      res3.pickLocations
        .sort((a, b) => {
          if (res3.hub === a.hub) return 1;
          else if (res3.hub === b.hub) return -1;
        })
        .sort((a, b) =>
          a.name > b.name &&
          ((res3.hub === a.hub && res3.hub === b.hub) || (res3.hub !== a.hub && res3.hub !== b.hub))
            ? 1
            : -1
        )
        .map((i) => i.name);

      var i,
        j,
        temparray,
        chunk = 30,
        arr = [];
      for (i = 0, j = res3.pickLocations.length; i < j; i += chunk) {
        temparray = res3.pickLocations.slice(i, i + chunk);
        arr.push(temparray);
      }
      setPickLocs(arr);
      setUser(res3);
      if (res3 && res3.locations) {
        localStorage.setItem("location", res3.locations[0].id);
        await fetchLocation(res3.locations[0].id);
      }
    } catch (err) {
      history.push(`/auth/login?rb=${history.location.pathname}`);
    }
  };

  const fetchLocation = async (id) => {
    try {
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/location/${id}`,
      });
      setCurrentLocation(data);
    } catch (err) {
      console.log(err);
    }
  };

  const getPatientData = async (id) => {
    try {
      if (id) {
        const url = "/patientmanagement/rest/interrogations/" + id + "/meta";
        return await fetchHelper.get({ url });
      }
    } catch (err) {
      addToast("API Error: " + err.message, {
        appearance: "error",
        autoDismiss: false,
      });
    }
  };

  const fetchUser = async (id) => {
    try {
      if (id) {
        let headerUrl = "/patientmanagement/rest/interrogations/" + id + "/meta";

        fetchHelper
          .get({ url: headerUrl })
          .then((header) => {
            setHeaderDetails(header);
          })
          .catch((err) => {
            addToast("API Error: " + err.message, {
              appearance: "error",
              autoDismiss: false,
            });
          });
      }
    } catch (err) {
      addToast("API Error: " + err.message, {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(err);
      // logout()
    }
  };

  const fetchMFR = async (id) => {
    try {
      if (id) {
        let interrogationUrl = "/patientmanagement/rest/interrogations/" + id;
        const mfrData = await fetchHelper.get({ url: interrogationUrl });
        setMFRData(mfrData);
      }
    } catch (err) {
      addToast("API Error: " + err.message, {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(err);
      // logout()
    }
  };

  const kFormatter = (num) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(0) + "k"
      : Math.sign(num) * Math.abs(num);
  };

  const _setUser = (u) => setUser(u);

  const _setAssignerInfo = (assigner) => setAssignerInfo(assigner);

  const updateHeaderDetails = (data) => setHeaderDetails(data);

  useEffect(() => {
    // This promise can be optional and NOT strictly dependant
    Promise.all([fetchReadPhysicians()]);

    // This promise needs to be strictly dependant
    Promise.allSettled([fetchU(), fetchAppConfig()]).then(() => {
      setUserAndToggleReady(true);
    });
  }, []);

  const fetchReadPhysicians = async () => {
    try {
      const url = "/patientmanagement/rest/users/available-physicians";
      const response = await fetchHelper.get({ url });
      setReadPhysicians(response);
    } catch (error) {
      console.log(error);
    }
  };

  const loadAssignerInfo = async (interrogationId) => {
    try {
      if (user.rcc && user.isServiceReport) {
        const url = `/patientmanagement/rest/v2/interrogations/${interrogationId}/assignment`;
        const response = await fetchHelper.get({ url });
        setAssignerInfo(response);
      }
    } catch (error) {
      console.log("ERROR", error);
    }
  };

  return (
    <LayoutContext.Provider
      value={{
        setAssignerInfo: _setAssignerInfo,
        assignerInfo,
        loadAssignerInfo,
        setUser: _setUser,
        user,
        headerDetails,
        mfrData,
        setMfrData: (d) => setMFRData(d),
        fetchUser,
        appConfigStructure,
        getPatientData,
        fetchMFR,
        updateHeaderDetails,
        readPhysicians,
        currentLocation,
        setCurrentLocation,
        intrOrderInfo,
        setIntrOrderInfo,
      }}
    >
      {localStorage.getItem("genToken") ? (
        <header>
          <div className="cont-flex max-hd">
            <div className="flex-1 logo">
              <NavLink
                id="geneva-logo"
                to="/reports3"
                style={{ padding: "initial", transform: "scale(0.5) translateX(-170px) translateY(-5px)" }}
                onClick={() => {
                  localStorage.setItem("selectedTab", "myReports");
                }}
              >
                <div className="geneva-logo" />
              </NavLink>
            </div>
            <div className="flex-1 nav-area no-print">
              <div className="nav-cont" style={{ position: "relative" }}>
                <nav>
                  <div className="nav">
                    <NavLink to="/reports3" className="btn btn-yellow btn-base64" id="Reports-button">
                      <span className="wrap-a">
                        <div className="pencil-icon" />
                        <span className="txt-nav"> Reports</span>
                      </span>
                    </NavLink>
                  </div>

                  <div id="menuOptionId" className="nav navMenu">
                    <a className="btn" id="menu-button">
                      <span className="wrap-a btn-base64">
                        <div className="grid-icon" />
                        <span className="txt-nav"> Menu</span>
                      </span>
                    </a>
                    <div className="submenuArea-cont menu" style={{ width: 600, height: 270 }}>
                      <div style={{ display: "flex", flexDirection: "row" }} className="submenuArea">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#f7f7f7",
                            flex: 1,
                          }}
                        >
                          <div className="profile-rat-container">
                            <li style={{ marginTop: 5 }}>Patient</li>
                            <li>
                              <NavLink
                                id="patientMenuOptionId"
                                activeClassName="selected"
                                exact
                                to="/patients3"
                                onClick={() => {
                                  localStorage.setItem("selectedTab", "patients");
                                }}
                              >
                                Profile Search
                              </NavLink>
                            </li>
                            {/* <li>
                              <a href="/reports3">Reports</a>
                            </li> */}
                            <li>
                              <NavLink
                                id="patient-insurance-menu-item"
                                activeClassName="selected"
                                to="/patients3/insurance"
                                onClick={() => {
                                  localStorage.setItem("selectedTab", "insurance");
                                }}
                              >
                                Insurance
                              </NavLink>
                            </li>
                            {hasAdtIntegration ? (
                              <li>
                                <NavLink
                                  id="patient-pending-menu-item"
                                  activeClassName="selected"
                                  to="/patients3/pending"
                                  onClick={() => localStorage.setItem("selectedTab", "pending")}
                                >
                                  Pending
                                </NavLink>
                              </li>
                            ) : null}
                            <li>
                              <NavLink
                                id="patient-merge-menu-item"
                                activeClassName="selected"
                                to="/patients3/patient-merge"
                                onClick={() => localStorage.setItem("selectedTab", "patient-merge")}
                              >
                                Merge
                              </NavLink>
                            </li>
                            {shouldShowEnrollment && (
                              <li>
                                <NavLink
                                  id="enrollmentMenuOptionId"
                                  activeClassName="selected"
                                  exact
                                  to="/patients3/enrollment"
                                  onClick={() => {
                                    localStorage.setItem("selectedTab", "enrollment");
                                  }}
                                >
                                  Enrollment
                                </NavLink>
                              </li>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#f7f7f7",
                            flex: 1,
                          }}
                        >
                          <div className="profile-rat-container">
                            <li style={{ marginTop: 5 }}>Administration</li>
                            {user &&
                            user.groups.findIndex(
                              (g) => g.name === "admin_group" || g.name === "isServiceReport_group"
                            ) !== -1 ? (
                              <>
                                <li>
                                  <NavLink to="/locations3" activeClassName="selected">
                                    Location
                                  </NavLink>
                                </li>
                                <li>
                                  <NavLink to="/users3" activeClassName="selected">
                                    Users
                                  </NavLink>
                                </li>
                              </>
                            ) : null}
                            <li>
                              <NavLink to="/reports3/preauth" activeClassName="selected">
                                Preauth
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/reports3/missed" activeClassName="selected">
                                Due Reports
                              </NavLink>
                            </li>
                            {patientEngagementTask ? (
                              <li>
                                <NavLink to="/reports3/patientengagement" activeClassName="selected">
                                  Task Queues
                                </NavLink>
                              </li>
                            ) : null}
                            <li>
                              <NavLink to="/billing3/export" activeClassName="selected">
                                eRecords
                              </NavLink>
                            </li>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#f7f7f7",
                            flex: 1,
                          }}
                        >
                          <div className="profile-rat-container">
                            <li style={{ marginTop: 5 }}>Analytics</li>
                            <li>
                              <NavLink to="/analytics3/cst" activeClassName="selected">
                                Statements
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/analytics3/events" activeClassName="selected">
                                Billable Stats
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/analytics3/unsigned" activeClassName="selected">
                                Unsigned Stats
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/analytics3/device" activeClassName="selected">
                                Device Population
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/analytics3/newdev" activeClassName="selected">
                                Devices Added
                              </NavLink>
                            </li>
                            <li>
                              <NavLink to="/analytics3/enabled" activeClassName="selected">
                                Devices Enabled
                              </NavLink>
                            </li>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="nav navProfile">
                    <a className="btn" id="profile-button">
                      <span className="wrap-a btn-base64">
                        <div className="user-icon" />
                        <span className="txt-nav"> Profile</span>
                      </span>
                    </a>
                    <div className="submenuArea-cont" style={{ maxWidth: "none", width: "auto" }}>
                      <ul className="submenuArea" style={{ maxWidth: "none" }}>
                        <div className="profile-rat-container">
                          {user ? (
                            <li style={{ borderBottom: "none" }}>
                              <span id="user-name">
                                Username: <b style={{ color: "#555" }}>{user.username}</b>
                                {` (${user.email})`}
                              </span>
                            </li>
                          ) : null}
                        </div>
                        <div className="profile-rat-container">
                          {user && user.locations && user.locations[0] ? (
                            <li style={{ borderBottom: "none" }}>
                              <span id="clinic-name">
                                Access to:{" "}
                                <b style={{ color: "#555" }}>
                                  {user.locations[0].name}
                                  {user.locations.length - 1 > 0 ? ` (+${user.locations.length - 1})` : ``}
                                </b>
                              </span>
                            </li>
                          ) : null}
                        </div>

                        {/* {user ? (
                          <div className="profile-rat-container">
                            <li>Restricted Access to:</li>
                          </div>
                        ) : null} */}
                        <div>
                          {pickLocs.map((o, i) => {
                            return (
                              <div className="profile-rat-container no-header" key={`pick-locs-${i}`}>
                                {o.map((i, ii) => (
                                  <li
                                    key={`pick-locs-${ii}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      onClickLocation(i);
                                    }}
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        position: "relative",
                                        marginLeft: 0,
                                        fontWeight: user?.hub !== i.hub ? "lighter" : "bolder",
                                      }}
                                    >
                                      {i.qprio ? (
                                        <div
                                          style={{
                                            color: JSON.parse(i.qprio).sla > 48 ? "red" : "#c9c9c9",
                                            width: "12px",
                                            marginRight: "5px",
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            fontWeight: "lighter",
                                          }}
                                        >
                                          ({kFormatter(JSON.parse(i.qprio).up)})
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            color: "#c9c9c9",
                                            width: "12px",
                                            marginRight: "5px",
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            fontWeight: "lighter",
                                          }}
                                        >
                                          (0)
                                        </div>
                                      )}
                                      {i.name}
                                    </div>
                                  </li>
                                ))}
                              </div>
                            );
                          })}
                        </div>

                        <div className="profile-logout-container">
                          <li id="logout-btn" className="btn btn-urgent" onClick={logout}>
                            LOGOUT
                          </li>
                        </div>
                      </ul>
                    </div>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </header>
      ) : null}

      <div className="cont-flex max-hd">
        {!isUserAndToggleReady ? (
          <div>Loading...</div>
        ) : (
          <div className="interrog-dash content-area">{children}</div>
        )}
      </div>
      <ModalAgreements user={user} />
    </LayoutContext.Provider>
  );
};

export default Layout;
